/**
 * The weather overlay's controls: a panel to set it up, then a bar to get on with.
 *
 * Two states and a deliberate difference between them. The panel is where you
 * choose the model and what you want drawn, and it takes up room because that is a
 * sit-down job. Minimised, it is one bar: arrows to nudge an hour at a time, a
 * slider to run through the days, the time it is showing, a handle to pull the
 * panel back, and a cross to turn the whole thing off. That is the state you are
 * in while you are actually looking at the chart, so it takes as little of it as
 * it can.
 *
 * The arrows matter more than they look. Dragging a slider on a phone in a
 * seaway is a poor way to move one hour, and one hour is the move you make most.
 *
 * Everything here writes through to the environment store, which is what MapView
 * already reads, rather than becoming a second opinion about what is on the chart.
 */
import { useEffect } from 'react';
import { useEnvStore } from '@/store/environment';
import {
  useWeatherOverlayStore,
  maxForecastH,
  OVERLAY_VARIABLES,
} from '@/store/weatherOverlay';
import { windColourSteps, DEFAULT_GREEN_FROM_KN, DEFAULT_RED_FROM_KN } from '@/map/windArrow';
import { useSettingsStore } from '@/store/settings';
import { fieldVariable, legendFor } from '@/map/fieldRamps';
import { OVERLAY_MODELS, WAVE_MODELS, resolutionLabel } from '@/weather/models';
import { useChartPlotterStore } from '@/store';
import { useRealWeatherStore } from '@/store/realWeather';
import { useWeatherFieldStore } from '@/store/weatherField';
import { providerHasRealWeather } from '@/chart-sources';
import type { WeatherSourceId } from '@/weather';

/**
 * Day initials for the length of the time bar, so a thumb on the slider knows
 * roughly which day it has landed in without reading the stamp. From the
 * PredictWind screenshots: their scrubber carries the day letters and it makes
 * a week of forecast navigable by feel.
 */
function dayInitials(maxH: number): string[] {
  const out: string[] = [];
  const start = new Date();
  for (let h = 0; h <= maxH; h += 24) {
    const d = new Date(start.getTime() + h * 3_600_000);
    out.push(['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getUTCDay()]);
  }
  return out;
}

/** "Thu 14:00 UT" for an hour offset from now. */
function stamp(offsetH: number): string {
  const d = new Date(Date.now() + offsetH * 3_600_000);
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${wd} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UT`;
}

export function WeatherOverlayBar() {
  const on = useWeatherOverlayStore((s) => s.on);
  const panelOpen = useWeatherOverlayStore((s) => s.panelOpen);
  // Subscribed so the legend chips and the boundary note follow a thresholds
  // change live (the ramp getters are already dynamic; the re-render is not).
  const windGreenFromKn = useSettingsStore((s) => s.windGreenFromKn);
  const windRedFromKn = useSettingsStore((s) => s.windRedFromKn);
  const modelId = useWeatherOverlayStore((s) => s.modelId);
  const variableId = useWeatherOverlayStore((s) => s.variableId);
  const forecastH = useWeatherOverlayStore((s) => s.forecastH);
  const close = useWeatherOverlayStore((s) => s.close);
  const showPanel = useWeatherOverlayStore((s) => s.showPanel);
  const minimise = useWeatherOverlayStore((s) => s.minimise);
  const setModelId = useWeatherOverlayStore((s) => s.setModelId);
  const waveModelId = useWeatherOverlayStore((s) => s.waveModelId);
  const setWaveModelId = useWeatherOverlayStore((s) => s.setWaveModelId);
  const setVariableId = useWeatherOverlayStore((s) => s.setVariableId);
  const windStyle = useWeatherOverlayStore((s) => s.windStyle);
  const setWindStyle = useWeatherOverlayStore((s) => s.setWindStyle);
  const setForecastH = useWeatherOverlayStore((s) => s.setForecastH);
  const nudge = useWeatherOverlayStore((s) => s.nudge);

  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  // What the fetch is doing. A model that returns nothing used to draw nothing
  // and say nothing, which is indistinguishable from the overlay being broken,
  // and cost a round trip to work out (fb-msgf00tl-1). Now it says so.
  const wxStatus = useRealWeatherStore((s) => s.status);
  const wxError = useRealWeatherStore((s) => s.error);
  const wxCount = useRealWeatherStore((s) => s.samples.length);
  // The wash is fetched separately from the arrows, so it fails separately too.
  // Usually that failure is "this model has no gusts", which is worth saying out
  // loud rather than leaving the chart looking broken.
  const fieldStatus = useWeatherFieldStore((s) => s.status);
  const fieldError = useWeatherFieldStore((s) => s.error);
  const realArea = providerHasRealWeather(chartProviderId);

  // Write through to the store the map reads. One place decides what is drawn.
  useEffect(() => {
    const env = useEnvStore.getState();
    if (!on) {
      if (env.overlayMode === 'weather') env.setOverlayMode('streams');
      return;
    }
    env.setOverlayMode('weather');
    env.setWeatherSource(modelId as WeatherSourceId);
    env.setWeatherForecastH(forecastH);
  }, [on, modelId, forecastH]);

  if (!on) return null;

  // The bar runs to the end of whichever model is actually feeding the wash.
  const maxH = maxForecastH(variableId === 'waves' ? waveModelId : modelId);
  const variable = OVERLAY_VARIABLES.find((v) => v.id === variableId);
  /** The ramp behind the wash, if this variable has one drawn. */
  const field = fieldVariable(variableId);

  if (panelOpen) {
    return (
      <div className="acp-wxpanel">
        <div className="acp-wxpanel-head">
          <strong>Weather overlay</strong>
          {/* Minimise next to the close, where hands expect it (fb-msie8z8m-2).
              The full-width button at the bottom does the same job, but a
              control below the fold is a control that does not exist, and the X
              alone reads as "your only way out turns the overlay off". */}
          {/* One flex group, so the header's space-between keeps the pair on
              the right edge together rather than stranding the minimise in the
              middle of the header. */}
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              type="button"
              className="acp-btn acp-wxbtn"
              onClick={minimise}
              aria-label="Minimise to the time bar, keeping the overlay drawn"
              title="Keep the overlay drawn and tuck this panel down to the bar"
            >
              −
            </button>
            <button
              type="button"
              className="acp-btn acp-wxbtn"
              onClick={close}
              aria-label="Turn the weather overlay off"
            >
              ✕
            </button>
          </div>
        </div>

        {!realArea && (
          <p className="acp-menu-note">
            The training charts are a made-up place, so there is no forecast for them. Switch the
            chart source to real water to draw one.
          </p>
        )}

        <label className="acp-fieldlabel">Show</label>
        <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
          {OVERLAY_VARIABLES.map((v) => (
            <button
              key={v.id}
              type="button"
              className={variableId === v.id ? 'on' : ''}
              disabled={!v.drawn}
              title={v.note}
              onClick={() => setVariableId(v.id)}
            >
              {v.name}
              {!v.drawn && ' ·'}
            </button>
          ))}
        </div>
        {variable && <p className="acp-menu-note">{variable.note}</p>}
        {field && (
          <>
            <div className="acp-wxkey">
              {legendFor(field).map((k) => (
                <span key={k.label} className="acp-wxkey-item">
                  <i style={{ background: k.colour }} />
                  {k.label}
                </span>
              ))}
              <span className="acp-wxkey-unit">{field.unit}</span>
            </div>
            {fieldStatus === 'error' && <p className="acp-menu-note">{fieldError}</p>}
          </>
        )}

        {variableId === 'wind' && (
          <>
            <label className="acp-fieldlabel" style={{ marginTop: 8 }}>Drawn as</label>
            <div className="acp-seg">
              <button
                type="button"
                className={windStyle === 'arrow' ? 'on' : ''}
                onClick={() => setWindStyle('arrow')}
                title="An arrow along the flow, coloured by strength, with the speed at its base"
              >
                Arrows
              </button>
              <button
                type="button"
                className={windStyle === 'barb' ? 'on' : ''}
                onClick={() => setWindStyle('barb')}
                title="The chart-table convention: feathers worth 5 and 10 knots"
              >
                Barbs
              </button>
            </div>
            <p className="acp-menu-note">
              Knots, {windGreenFromKn === DEFAULT_GREEN_FROM_KN && windRedFromKn === DEFAULT_RED_FROM_KN
                ? 'on Beaufort boundaries'
                : 'on our adjusted boundaries (Wind colours, in Boat settings)'}
              : {windColourSteps().map((k) => k.label).join(', ')}. The
              arrow points the way the wind is going, and the number and every readout in the app
              are the direction it blows from.
            </p>
          </>
        )}

        <label className="acp-fieldlabel" style={{ marginTop: 8 }}>
          {variableId === 'waves' ? 'Wave model and resolution' : 'Model and resolution'}
        </label>
        {variableId === 'waves' && (
          <p className="acp-menu-note">
            The wave models are their own list: sea state is forecast by different models than the
            wind, and pretending otherwise would hide which one you are reading.
          </p>
        )}
        <div className="acp-wxmodels">
          {(variableId === 'waves' ? WAVE_MODELS : OVERLAY_MODELS).map((m) => {
            const on = variableId === 'waves' ? waveModelId === m.id : modelId === m.id;
            const pick = variableId === 'waves' ? setWaveModelId : setModelId;
            return (
              <button
                key={m.id}
                type="button"
                className={`acp-wxmodel${on ? ' on' : ''}`}
                onClick={() => pick(m.id)}
              >
                <strong>{m.name}</strong>
                <span className="acp-menu-note">
                  {m.provider} · {resolutionLabel(m)} · {m.days} days
                </span>
              </button>
            );
          })}
        </div>

        <button type="button" className="acp-btn primary full" style={{ marginTop: 8 }} onClick={minimise}>
          Show it on the chart
        </button>
      </div>
    );
  }

  return (
    <div className="acp-wxbar-wrap">
      {/* The scale stays on screen while the panel is away. Reading a colour off a
          chart means nothing without the numbers next to it, and having to reopen
          the panel to remember what orange was is exactly the sort of small
          friction that stops people using a thing at all. */}
      {field && (
        <div className="acp-wxscale" aria-hidden="true">
          {legendFor(field).map((k) => (
            <span key={k.label} style={{ background: k.colour }}>
              {k.label}
            </span>
          ))}
          <span className="acp-wxscale-unit">{field.unit}</span>
        </div>
      )}
      <div className="acp-wxbar">
      <button
        type="button"
        className="acp-btn acp-wxbtn"
        onClick={showPanel}
        aria-label="Change the model or what is shown"
        title="Model and variable"
      >
        ⌃
      </button>
      <button
        type="button"
        className="acp-btn acp-wxbtn"
        onClick={() => setForecastH(0)}
        disabled={forecastH === 0}
        aria-label="Back to now"
        title="Jump the forecast back to now (fb-mslh2zyd-1)"
      >
        Now
      </button>
      <button
        type="button"
        className="acp-btn acp-wxbtn"
        onClick={() => nudge(-1)}
        aria-label="One hour earlier"
      >
        ◀
      </button>
      <div className="acp-wxbar-mid">
        <div className="acp-wxdays" aria-hidden="true">
          {dayInitials(maxH).map((l, i) => (
            <span key={i} className={i === Math.floor(forecastH / 24) ? 'on' : ''}>
              {l}
            </span>
          ))}
        </div>
        <input
          type="range"
          min={0}
          max={maxH}
          step={1}
          value={forecastH}
          aria-label="Forecast time, hours from now"
          onChange={(e) => setForecastH(Number(e.target.value))}
        />
        <span className="acp-wxbar-time">
          {forecastH === 0 ? 'Now' : `+${forecastH} h`} · {stamp(forecastH)}
          {wxStatus === 'loading' && ' · fetching…'}
          {wxStatus === 'error' && ` · no data: ${wxError ?? 'fetch failed'}`}
          {wxStatus === 'ready' && wxCount === 0 && ' · this model has nothing here'}
          {fieldStatus === 'error' && ` · ${fieldError}`}
        </span>
      </div>
      <button
        type="button"
        className="acp-btn acp-wxbtn"
        onClick={() => nudge(1)}
        aria-label="One hour later"
      >
        ▶
      </button>
      <button
        type="button"
        className="acp-btn acp-wxbtn"
        onClick={close}
        aria-label="Turn the weather overlay off"
      >
        ✕
      </button>
      </div>
    </div>
  );
}
